import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import uuid from '../../services/uuid';

import eventBus from '../../services/globalEventBus';
import TimeoutPromise from '../../services/TimeoutPromise';
import trainingInstanceService from '../../services/trainingInstanceService';
import workflowService from '../../services/workflowService';
import cacheService from '../../services/cacheService';
import { handleError } from '../../utils/apiUtils';

export const StepsStateContext = React.createContext();
export const StepsDispatchContext = React.createContext();

const EMPTY = Symbol('Empty Context');

export const useStepsState = () => {
  const context = React.useContext(StepsStateContext);
  if (context === EMPTY) {
    throw new Error('useStepsState must be used within a StepsProvider');
  }
  return context;
};

export const useStepsDispatch = () => {
  const context = React.useContext(StepsDispatchContext);
  if (context === EMPTY) {
    throw new Error('useStepsDispatch must be used within a StepsProvider');
  }
  return context;
};

const ASYNCSTATUS = {
  IDLE: 'IDLE',
  COMPLETING: 'COMPLETING',
  FETCHING: 'FETCHING',
  SAVING: 'SAVING',
  RESOLVED: 'RESOLVED',
  REJECTED: 'REJECTED',
};

export const ACTIONS = {
  SET_TRAINING: 'set-training',
  SET_MOBILE_TRAINING_MENU: 'set-mobile-training-menu',
  GET_CURRENT_COURSE: 'get-current-course',
  UPDATE_CURRENT_STEP: 'update-current-step',
  ADD_ASSESSMENT_TASK_RESPONSE_ID: 'add-assessment-task-response-id',
  ADD_TO_COMPLETING_STEP_INDEXES: 'add-to-completing-step-indexes',
  REMOVE_FROM_COMPLETING_STEP_INDEXES: 'remove-from-completing-step-indexes',
  FINISH_COURSE_IN_WORKFLOW: 'finish-course-in-workflow',
  SET_SHOW_TRAINING_COMPLETED_MODAL: 'set-show-training-completed-modal',
  CHANGE_STEP: 'change-step',
};

const initialStepIndex = (tasks, stepInstanceId) => {
  const stepIndex =
    stepInstanceId === undefined
      ? -1
      : tasks.findIndex(function (task) {
          return task.id.id === stepInstanceId;
        });
  return stepIndex === -1 ? 0 : stepIndex;
};

const updateCourseProgress = course => {
  let updatedProgress,
    completedSteps = course.tasks.filter(step => step.status === 'complete').length,
    totalTasks = course.tasks.length;

  if (course.progress.steps) {
    updatedProgress = {
      steps: {
        total: totalTasks,
        completed: completedSteps,
      },
      percent: (completedSteps / totalTasks) * 100,
      _type: completedSteps / totalTasks === 1 ? 'Finished' : 'Started',
    };
  } else {
    updatedProgress = completedSteps / totalTasks;
  }

  return updatedProgress;
};

const getCurrentCourseIndex = (training, isWorkflow, stepInstanceId) => {
  if (!isWorkflow) return 0;
  if (training?.progress?._type === 'Finished') return 0;

  if (stepInstanceId) {
    const courseIndex = training?.courses?.findIndex(
      course => course?.progress?._type !== 'Finished' && course.tasks?.some(task => task.id.id === stepInstanceId)
    );
    if (courseIndex >= 0) return courseIndex;
  }

  return training?.courses?.findIndex(course => course?.progress?._type !== 'Finished');
};

const updateCurrentCourse = (course, newStatus, uploadedAssetUrl, currentStepIndex) => {
  const updatedCourse = {
    ...course,
    tasks: course.tasks.map((courseTask, i) => {
      if (i == currentStepIndex) {
        return {
          ...courseTask,
          status: newStatus,
          ...(uploadedAssetUrl && {
            task: {
              ...courseTask.task,
              uploadedAssetUrl,
            },
          }),
        };
      } else {
        return courseTask;
      }
    }),
  };
  return {
    ...updatedCourse,
    progress: updateCourseProgress(updatedCourse),
  };
};

const updateCurrentCourseResponseId = (course, newStatus, responseId, currentStepIndex) => {
  const updatedCourse = {
    ...course,
    tasks: course.tasks.map((courseTask, i) => {
      if (i === currentStepIndex) {
        return {
          ...courseTask,
          ...(newStatus && { status: newStatus }),
          ...(responseId && {
            task: {
              ...courseTask.task,
              responseId,
            },
          }),
        };
      } else {
        return courseTask;
      }
    }),
  };
  return updatedCourse;
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_TRAINING: {
      return {
        ...state,
        training: action.payload.training,
      };
    }
    case ACTIONS.FINISH_COURSE_IN_WORKFLOW: {
      const training = action.payload.training;
      return {
        ...state,
        training,
      };
    }
    case ACTIONS.ADD_ASSESSMENT_TASK_RESPONSE_ID: {
      const { responseId, newStatus } = action.payload;
      const { currentCourseIndex, training, currentStepIndex, isWorkflow } = state;
      return isWorkflow
        ? (() => {
            const updatedCourses = training.courses.map((course, i) =>
              i === currentCourseIndex
                ? updateCurrentCourseResponseId(course, newStatus, responseId, currentStepIndex)
                : course
            );

            return {
              ...state,
              training: {
                ...training,
                courses: updatedCourses,
              },
            };
          })()
        : {
            ...state,
            training: updateCurrentCourseResponseId(training, newStatus, responseId, currentStepIndex),
          };
    }
    case ACTIONS.SET_MOBILE_TRAINING_MENU: {
      return {
        ...state,
        isMobileTrainingMenuOpen: action.payload.isMobileTrainingMenuOpen,
      };
    }
    case ACTIONS.UPDATE_CURRENT_STEP: {
      const { newStatus, uploadedAssetUrl } = action.payload;
      const { currentCourseIndex, training, currentStepIndex, isWorkflow } = state;

      return isWorkflow
        ? (() => {
            const updatedCourses = training.courses.map((course, i) =>
              i === currentCourseIndex
                ? updateCurrentCourse(course, newStatus, uploadedAssetUrl, currentStepIndex)
                : course
            );

            const completedCourses = updatedCourses.filter(course => course.progress._type === 'Finished');
            const percent = (completedCourses.length / training.courses.length) * 100;

            return {
              ...state,
              training: {
                ...training,
                courses: updatedCourses,
                progress: {
                  _type: percent === 100 ? 'Finished' : 'Started',
                  percent: percent,
                  steps: {
                    total: training.courses.length,
                    completed: completedCourses.length,
                  },
                },
              },
            };
          })()
        : {
            ...state,
            training: updateCurrentCourse(training, newStatus, uploadedAssetUrl, currentStepIndex),
          };
    }
    case ACTIONS.CHANGE_STEP: {
      return {
        ...state,
        currentStepIndex: action.payload.stepIndex,
        currentCourseIndex: action.payload.courseIndex,
        ...(action.payload.setShowLockedStep
          ? { setShowLockedStep: action.payload.setShowLockedStep }
          : { setShowLockedStep: false }),
        ...(action.payload.courseIndex && { currentCourse: action.payload.courseIndex }),
        ...(action.payload.isMobileTrainingMenuOpen
          ? {
              isMobileTrainingMenuOpen: action.payload.isMobileTrainingMenuOpen,
            }
          : { isMobileTrainingMenuOpen: false }),
      };
    }
    case ACTIONS.ADD_TO_COMPLETING_STEP_INDEXES: {
      const key = `${action.payload.currentCourseIndex}-${action.payload.currentStepIndex}`;
      const newIndexes = [...state.completingStepIndexes, key];
      return {
        ...state,
        completingStepIndexes: newIndexes,
        status: ASYNCSTATUS.COMPLETING,
      };
    }
    case ACTIONS.REMOVE_FROM_COMPLETING_STEP_INDEXES: {
      const key = `${action.payload.currentCourseIndex}-${action.payload.currentStepIndex}`;
      return {
        ...state,
        status: ASYNCSTATUS.IDLE,
        completingStepIndexes: state.completingStepIndexes.filter(i => i !== key),
      };
    }
    case ACTIONS.SET_SHOW_TRAINING_COMPLETED_MODAL: {
      return {
        ...state,
        showTrainingCompletedModal: action.payload.showTrainingCompletedModal,
        completeModalShown: true,
      };
    }
    default:
      break;
  }
  return state;
};

export const useSteps = () => {
  const stepsData = useStepsState();
  const { isWorkflow, training, currentStepIndex, currentCourseIndex, status } = stepsData;

  const dispatch = useStepsDispatch();

  const currentCourse = isWorkflow ? training?.courses[currentCourseIndex] : training;
  const currentStep = currentCourse?.tasks?.length > 0 ? currentCourse?.tasks[currentStepIndex] : {};
  const numCompletedSteps = currentCourse?.tasks?.filter(step => step.status === 'complete').length;
  const isLastStepInCourse = numCompletedSteps + 1 === currentCourse?.tasks?.length;
  const incompleteStepIndex = currentCourse?.tasks?.findIndex(step => step.status !== 'complete');
  const lastStepIndex =
    isLastStepInCourse && incompleteStepIndex >= 0 ? incompleteStepIndex : currentCourse?.tasks?.length - 1;

  return [
    {
      ...stepsData,
      currentStep,
      currentCourse: {
        ...currentCourse,
        progress: {
          ...currentCourse?.progress,
          _type: 'Started',
        },
      },
      currentStepIsIncomplete: currentStep.status !== 'complete',
      currentStepIsInProgress: currentStep.status === 'in_progress',
      numCompletedSteps,
      lastStepIndex,
      isLastStepInCourse,
      isCompletingStep: status === 'COMPLETING',
    },
    dispatch,
  ];
};

export const StepsProvider = ({ children, trainingInput, stepInstanceId, user }) => {
  const isWorkflow = Boolean(trainingInput?.courses);
  const currentCourseIndex = getCurrentCourseIndex(trainingInput, isWorkflow, stepInstanceId);

  const currentCourse = isWorkflow ? trainingInput.courses[currentCourseIndex] : trainingInput;
  const currentStepIndex = initialStepIndex(currentCourse?.tasks, stepInstanceId);

  const initialState = {
    isWorkflow,
    numTotalCourses: trainingInput?.courses?.length,
    stepInstanceId,
    training: trainingInput,
    currentCourseIndex,
    currentStepIndex,
    completingStepIndexes: [],
    isMobileTrainingMenuOpen: false,
    status: ASYNCSTATUS.IDLE,
    user,
    setShowLockedStep: false,
    lastStepIndex: currentCourse?.tasks?.length - 1,
    isOrgUser: user?.isOrgUser,
    assignedUser: isWorkflow ? trainingInput?.assigneeId?.id : trainingInput?.assignedUser,
    assigningOrg: isWorkflow ? trainingInput?.assignorId?.id : trainingInput?.assignedUser,
    showTrainingCompletedModal: false,
    completeModalShown: isWorkflow ? trainingInput.progress._type === 'Finished' : trainingInput.status === 'complete',
  };
  const [state, dispatch] = React.useReducer(reducer, initialState);

  return (
    <StepsStateContext.Provider value={state}>
      <StepsDispatchContext.Provider value={dispatch}>{children}</StepsDispatchContext.Provider>
    </StepsStateContext.Provider>
  );
};

StepsProvider.propTypes = {
  trainingInput: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
  stepInstanceId: PropTypes.string,
  user: PropTypes.object,
};

export const selectStep = (dispatch, payload) => {
  dispatch({
    type: ACTIONS.CHANGE_STEP,
    payload,
  });
};

export const setTraining = (dispatch, payload) => {
  dispatch({ type: ACTIONS.SET_TRAINING, payload: { training: payload } });
};

export const toggleMobileTrainingMenu = (dispatch, payload) => {
  dispatch({ type: ACTIONS.SET_MOBILE_TRAINING_MENU, payload: { isMobileTrainingMenuOpen: payload } });
};

export const updateCurrentStepInCourse = (dispatch, payload) => {
  dispatch({
    type: ACTIONS.UPDATE_CURRENT_STEP,
    payload: {
      newStatus: payload?.newStatus || 'complete',
      uploadedAssetUrl: payload?.uploadedAssetUrl || undefined,
    },
  });
};

export const addAssessmentTaskResponseId = (dispatch, payload) => {
  const responseId = uuid.generate();
  dispatch({
    type: ACTIONS.ADD_ASSESSMENT_TASK_RESPONSE_ID,
    payload: {
      responseId,
      newStatus: payload?.newStatus || undefined,
    },
  });
  return responseId;
};

export const setShowTrainingCompletedModal = (dispatch, payload) => {
  dispatch({
    type: ACTIONS.SET_SHOW_TRAINING_COMPLETED_MODAL,
    payload,
  });
};

const subscribeToEvent = eventName =>
  TimeoutPromise({ seconds: 60 }, resolve => {
    const unsubscribe = eventBus.subscribe(eventName, event => {
      if (event.eventType === eventName) {
        unsubscribe();
        resolve();
      }
    });
  });

export const useCompleteStep = () => {
  const { isWorkflow, training, currentStepIndex, currentCourseIndex, user } = useStepsState();
  const dispatch = useStepsDispatch();

  const currentCourse = isWorkflow ? training?.courses[currentCourseIndex] : training;
  const currentStep = currentCourse?.tasks?.length > 0 ? currentCourse?.tasks[currentStepIndex] : undefined;
  const numCompletedSteps = currentCourse?.tasks?.filter(step => step.status === 'complete').length;
  const isLastStepInCourse = numCompletedSteps + 1 === currentCourse?.tasks?.length;

  const completeStep = useCallback(
    deletedStepOpt => {
      if (currentStep.status === 'complete') return;

      // Just in case the currentStepIndex/currentCourseIndex in state changes while we are running the promise
      // we are setting completedIndex/completedCourseIndex to send to the reducer so it doesn't change
      const completedIndex = currentStepIndex;
      const completedCourseIndex = currentCourseIndex;

      dispatch({
        type: ACTIONS.ADD_TO_COMPLETING_STEP_INDEXES,
        payload: { currentStepIndex: completedIndex, currentCourseIndex: completedCourseIndex },
      });

      const isLastCourseInWF = isWorkflow && training.progress.steps.completed + 1 === training.progress.steps.total;

      const completeStepPromises = [
        trainingInstanceService.completeStep(currentCourse.id, currentStep, deletedStepOpt, user?.lastSelectedAccount),
      ];

      if (isWorkflow && isLastStepInCourse) {
        const nextCourse = training.courses[currentCourseIndex + 1];
        const nextCourseAlreadyCreated = nextCourse?._type === 'CourseInstanceDetail';
        const hasNextCourse = nextCourse !== undefined;

        if (isLastCourseInWF) {
          completeStepPromises.push(subscribeToEvent('WorkflowInstanceEnded'));
        } else if (hasNextCourse && !nextCourseAlreadyCreated) {
          completeStepPromises.push(subscribeToEvent('TaskListInstanceCreated'));
        }
      }

      Promise.all(completeStepPromises)
        .then(() => {
          cacheService.removeAllInPath(`/v1/trainingOverview`);
          if (isWorkflow && isLastStepInCourse) {
            workflowService.getWorkflowInstanceDetailById(training.id).then(training => {
              dispatch({ type: ACTIONS.FINISH_COURSE_IN_WORKFLOW, payload: { training, isLastCourseInWF } });
            });
          } else {
            cacheService.removeAllInPath(`/v1/user-tasklists/${training.id}`);
            updateCurrentStepInCourse(dispatch);
          }
        })
        .catch(() => {
          handleError(
            'Oops, we’re unable to complete training at this time. We’re sorry for the inconvenience. Please try again later.'
          );
        })
        .finally(() => {
          dispatch({
            type: ACTIONS.REMOVE_FROM_COMPLETING_STEP_INDEXES,
            payload: { currentStepIndex: completedIndex, currentCourseIndex: completedCourseIndex },
          });
        });
    },
    [
      currentCourse.id,
      currentCourseIndex,
      currentStep,
      currentStepIndex,
      dispatch,
      isLastStepInCourse,
      isWorkflow,
      training,
      user?.lastSelectedAccount,
    ]
  );

  return { completeStep };
};
